import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { UserButton, useUser } from '@clerk/react';
import { useTranslation } from '@yorukiri/i18n';
import { colors } from '@yorukiri/ui/tokens';
import { Book, Plus, Search, Settings, Sparkles } from './icons';
import { PageTree } from './PageTree';
import { useCreatePage, usePageChildren } from '../lib/queries';

export function Sidebar() {
  const { t } = useTranslation('nav');
  const navigate = useNavigate();
  const { data: rootPages, isLoading } = usePageChildren(null);
  const createPage = useCreatePage();

  const handleNewPage = async () => {
    const page = await createPage.mutateAsync({});
    navigate(`/pages/${page.id}`);
  };

  return (
    <aside
      style={{
        background: colors.surface2,
        borderRight: `1px solid ${colors.line}`,
        padding: '20px 14px',
        display: 'flex',
        flexDirection: 'column',
        fontSize: 13,
        overflowY: 'auto',
      }}
    >
      <Logo />
      <SearchBar />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginBottom: 20 }}>
        <SidebarLink to="/" icon={<Book size={13} />}>{t('library')}</SidebarLink>
        <SidebarLink to="/stats" icon={<Sparkles size={13} />}>{t('stats')}</SidebarLink>
        <SidebarLink to="/settings" icon={<Settings size={13} />}>{t('settings')}</SidebarLink>
      </div>

      <Section
        title={t('pages')}
        action={
          <button
            onClick={handleNewPage}
            disabled={createPage.isPending}
            title="New page"
            aria-label="New page"
            style={{
              background: 'transparent',
              border: 'none',
              color: colors.ink3,
              cursor: 'pointer',
              padding: 2,
              lineHeight: 0,
              opacity: createPage.isPending ? 0.4 : 1,
            }}
          >
            <Plus size={13} />
          </button>
        }
      >
        {isLoading && (
          <div style={{ color: colors.ink3, fontSize: 12, padding: '5px 8px' }}>…</div>
        )}
        {!isLoading && (rootPages?.length ?? 0) === 0 && (
          <div style={{ color: colors.ink3, fontSize: 11.5, padding: '5px 8px', fontStyle: 'italic' }}>
            No pages yet.
          </div>
        )}
        {rootPages && <PageTree rootPages={rootPages} />}
      </Section>

      <div
        style={{
          marginTop: 'auto',
          paddingTop: 16,
          borderTop: `1px solid ${colors.line}`,
        }}
      >
        <Footer />
      </div>
    </aside>
  );
}

function Logo() {
  return (
    <div
      style={{
        fontWeight: 600,
        letterSpacing: '-0.02em',
        fontSize: 22,
        marginBottom: 20,
        display: 'flex',
        alignItems: 'baseline',
        gap: 6,
      }}
    >
      Yorukiri
      <span
        style={{
          display: 'inline-block',
          width: 5,
          height: 5,
          background: colors.accent,
          borderRadius: '50%',
          transform: 'translateY(-4px)',
        }}
      />
    </div>
  );
}

function SearchBar() {
  const { t } = useTranslation('nav');
  return (
    <div
      style={{
        background: 'rgba(24, 22, 15, 0.05)',
        borderRadius: 6,
        padding: '6px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 20,
        color: colors.ink3,
        fontSize: 12,
      }}
    >
      <Search size={12} />
      <span>{t('search_placeholder')}</span>
      <kbd
        style={{
          marginLeft: 'auto',
          fontVariantNumeric: 'tabular-nums',
          fontSize: 10,
          background: 'rgba(24,22,15,0.05)',
          padding: '1px 5px',
          borderRadius: 3,
        }}
      >
        {t('shortcut_search')}
      </kbd>
    </div>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div style={{ marginBottom: 22 }}>
      <h4
        style={{
          fontSize: 10,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: colors.ink3,
          fontWeight: 500,
          margin: '0 0 8px 0',
          padding: '0 6px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {title}
        {action}
      </h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{children}</div>
    </div>
  );
}

function SidebarLink({
  to,
  icon,
  children,
}: {
  to: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <NavLink to={to} end style={linkStyle}>
      <span style={{ width: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </span>
      <span>{children}</span>
    </NavLink>
  );
}

function Footer() {
  const { user } = useUser();
  const name = user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? '';
  const email = user?.primaryEmailAddress?.emailAddress ?? '';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <UserButton afterSignOutUrl="/sign-in" />
      <div style={{ fontSize: 12, lineHeight: 1.3, overflow: 'hidden', minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {name}
        </div>
        <div
          style={{
            color: colors.ink3,
            fontSize: 11,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {email}
        </div>
      </div>
    </div>
  );
}

const linkStyle = ({ isActive }: { isActive: boolean }) =>
  ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 8px',
    borderRadius: 5,
    color: isActive ? colors.surface : colors.ink2,
    background: isActive ? colors.accent : 'transparent',
    textDecoration: 'none',
    fontSize: 12.5,
  }) as const;
